import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DashLayout from '../../components/DashLayout';
import { useAuth } from '../../context/AuthContext';

const nav = [
  { to: '/dashboard', icon: '📊', label: 'Dashboard' },
  { to: '/dashboard/chats', icon: '💬', label: 'Live Chats' },
  { to: '/dashboard/leads', icon: '👥', label: 'Leads' },
  { to: '/dashboard/crm', icon: '📋', label: 'CRM' },
  { to: '/dashboard/agents', icon: '👤', label: 'Agents' },
  { to: '/dashboard/tickets', icon: '🎫', label: 'Support Tickets' },
  { to: '/dashboard/ai-bot', icon: '🤖', label: 'AI Chat Bot' },
  { to: '/dashboard/api', icon: '🔑', label: 'API Access' },
  { to: '/dashboard/branding', icon: '🎨', label: 'Branding' },
  { to: '/dashboard/widget', icon: '🔌', label: 'Widget & Script' },
  { to: '/dashboard/contact-form', icon: '📝', label: 'Contact Form' },
  { to: '/dashboard/analytics', icon: '📈', label: 'Analytics' },
  { to: '/dashboard/billing', icon: '💳', label: 'Billing' },
  { to: '/dashboard/settings', icon: '⚙️', label: 'Settings' }
];

const COLORS = ['#00d55b','#185FA5','#7c3aed','#0f766e','#b45309','#be185d','#1d4ed8','#15803d','#991b1b','#0891b2'];

export default function BusinessBranding() {
  const { user } = useAuth();
  const [form, setForm] = useState({ company_display_name: '', brand_color: '#00d55b', show_powered_by: 1, logo_url: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get('/api/business/profile').then(r => {
      const d = r.data.data;
      setForm({
        company_display_name: d.company_display_name || d.company_name || '',
        brand_color: d.brand_color || '#00d55b',
        show_powered_by: d.show_powered_by ?? 1,
        logo_url: d.logo_url || ''
      });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      await axios.put('/api/business/branding', form);
      toast.success('Branding saved successfully!');
    } catch { toast.error('Failed to save branding'); }
    finally { setLoading(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <DashLayout navItems={nav} role="Business Admin">
      <div style={{ padding: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Branding Settings</h1>
        <p style={{ color: '#555', fontSize: 13, marginBottom: 28 }}>Customize how your chat widget looks to visitors</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 24 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

            {/* Company Name */}
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Company Display Name</h3>
              <input
                value={form.company_display_name}
                onChange={e => set('company_display_name', e.target.value)}
                placeholder="Your Company Name"
              />
              <p style={{ color: '#444', fontSize: 11, marginTop: 6 }}>This name appears in the chat widget header</p>
            </div>

            {/* Logo Upload */}
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Company Logo</h3>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                {/* Logo Preview */}
                <div style={{ width: 80, height: 80, borderRadius: 12, background: '#1a1a1a', border: '1px solid #1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                  {form.logo_url ? (
                    <img src={form.logo_url} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }} onError={e => e.target.style.display='none'} />
                  ) : (
                    <span style={{ fontSize: 28 }}>🏢</span>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Logo URL</label>
                  <input
                    value={form.logo_url}
                    onChange={e => set('logo_url', e.target.value)}
                    placeholder="https://yoursite.com/logo.png"
                  />
                  <p style={{ fontSize: 11, color: '#444', marginTop: 6 }}>Paste a direct link to your logo image (PNG, JPG, SVG)</p>
                </div>
              </div>
            </div>

            {/* Brand Color */}
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Brand Color</h3>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                {COLORS.map(c => (
                  <div key={c} onClick={() => set('brand_color', c)}
                    style={{ width: 34, height: 34, borderRadius: '50%', background: c, cursor: 'pointer', border: `3px solid ${form.brand_color === c ? '#fff' : 'transparent'}`, boxShadow: form.brand_color === c ? '0 0 0 2px ' + c : 'none', transition: 'all 0.15s' }} />
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input
                  type="color"
                  value={form.brand_color}
                  onChange={e => set('brand_color', e.target.value)}
                  style={{ width: 44, height: 44, padding: 2, background: '#1a1a1a', border: '1px solid #1e1e1e', borderRadius: 8, cursor: 'pointer' }}
                />
                <input
                  value={form.brand_color}
                  onChange={e => set('brand_color', e.target.value)}
                  placeholder="#00d55b"
                  style={{ width: 120 }}
                />
                <div style={{ width: 44, height: 44, borderRadius: 8, background: form.brand_color }} />
              </div>
            </div>



            <button onClick={save} disabled={loading}
              style={{ padding: '13px', background: '#00d55b', color: '#000', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer', border: 'none', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Saving...' : '💾 Save Branding'}
            </button>
          </div>

          {/* Live Preview */}
          <div style={{ position: 'sticky', top: 24 }}>
            <div style={{ fontSize: 12, color: '#555', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Live Preview</div>
            <div style={{ background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: 16, overflow: 'hidden', boxShadow: '0 8px 30px rgba(0,0,0,0.4)' }}>
              {/* Widget Header */}
              <div style={{ background: '#111', padding: '13px 15px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #1e1e1e' }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: form.brand_color, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                  {form.logo_url ? (
                    <img src={form.logo_url} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 3 }} onError={e => e.target.style.display='none'} />
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{form.company_display_name || user?.company_name || 'Your Company'}</div>
                  <div style={{ fontSize: 11, color: '#888', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00d55b' }} />
                    We're online
                  </div>
                </div>
              </div>
              {/* Sample messages */}
              <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 180 }}>
                <div style={{ background: '#1a1a1a', padding: '8px 12px', borderRadius: 10, borderBottomLeftRadius: 2, fontSize: 12, color: '#ddd', maxWidth: '80%' }}>
                  Hi! How can we help you today? 👋
                </div>
                <div style={{ background: form.brand_color, padding: '8px 12px', borderRadius: 10, borderBottomRightRadius: 2, fontSize: 12, color: '#000', fontWeight: 500, maxWidth: '80%', alignSelf: 'flex-end' }}>
                  I need some help!
                </div>
              </div>
              {/* Input row */}
              <div style={{ padding: '10px 12px', borderTop: '1px solid #1e1e1e', display: 'flex', gap: 8 }}>
                <div style={{ flex: 1, background: '#1a1a1a', border: '1px solid #1e1e1e', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#444' }}>Type a message...</div>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: form.brand_color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: 6, fontSize: 10, color: '#333', background: '#0a0a0a' }}>Powered by MSChat</div>
            </div>

            {/* Bubble preview */}
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ width: 50, height: 50, borderRadius: '50%', background: form.brand_color, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashLayout>
  );
}